import type { Landmark } from '../../../data/types';
import type { Ring } from '../../../geo/types';
import { vesselOutline } from './churchVessel';
import { conventEnclosureRings } from './conventEnclosure';
import { conventRangeRect } from './conventRange';
import { corners } from './genericBlock';
import reading from './sintClaraPlan.json';
import { toLayout, type PlanFrame } from './planFrame';

export function sintClaraReading(landmark: Landmark) {
  if (landmark.id !== 'landmark-sint-clara') throw new Error(`No Clara plan for ${landmark.id}`);
  return reading;
}

export function sintClaraFrame(landmark: Landmark): PlanFrame {
  const x = landmark.params['anchorX'],
    y = landmark.params['anchorY'];
  if (typeof x !== 'number' || typeof y !== 'number')
    throw new Error(`${landmark.id} lacks its component anchor`);
  return { origin: [x, y], heading: landmark.heading, ring: [] };
}

export const sintClaraChapel = (landmark: Landmark) => ({
  ...sintClaraReading(landmark).chapel,
  apse: 'flat' as const,
});

export function sintClaraRangeFrame(
  plan: PlanFrame,
  part: { readonly u: number; readonly v: number; readonly angleDeg: number }
): PlanFrame {
  return {
    origin: toLayout(plan, [part.u, part.v]),
    heading: plan.heading + (part.angleDeg * Math.PI) / 180,
    ring: [],
  };
}

/** Grounded bodies only: neither court nor canal entry becomes a precinct slab. */
export function sintClaraFoundations(landmark: Landmark): Ring[] {
  const plan = sintClaraFrame(landmark);
  return [
    vesselOutline(sintClaraChapel(landmark)).map((p) => toLayout(plan, p)),
    ...sintClaraReading(landmark).ranges.flatMap((part) => {
      const rect = conventRangeRect(part),
        frame = sintClaraRangeFrame(plan, part),
        centre = 'passageU' in part ? part.passageU : 0;
      return (
        part.passage
          ? [
              { ...rect, u1: centre - part.passage / 2 },
              { ...rect, u0: centre + part.passage / 2 },
            ]
          : [rect]
      ).map((r) => corners(r).map((p) => toLayout(frame, p)));
    }),
  ];
}

export function sintClaraObstacles(landmark: Landmark): Ring[] {
  const plan = sintClaraFrame(landmark);
  return [
    ...sintClaraFoundations(landmark),
    ...conventEnclosureRings(sintClaraReading(landmark).walls).map((r) =>
      r.map((p) => toLayout(plan, p))
    ),
  ];
}

export function sintClaraFloors(landmark: Landmark) {
  const plan = sintClaraFrame(landmark),
    reading = sintClaraReading(landmark);
  return [
    ...reading.floors.map((f) => ({ ...f, ring: f.ring.map((p) => [p[0]!, p[1]!] as const) })),
    ...reading.ranges
      .filter((p) => p.passage)
      .map((p) => {
        const centre = 'passageU' in p ? p.passageU : 0;
        return {
          ring: corners({
            u0: centre - p.passage / 2,
            u1: centre + p.passage / 2,
            v0: -p.width / 2,
            v1: p.width / 2,
          }).map((q) =>
            toLayout({ origin: [p.u, p.v], heading: (p.angleDeg * Math.PI) / 180, ring: [] }, q)
          ),
          height: 0.025,
          garden: false,
        };
      }),
  ].map((f) => ({ ...f, ring: f.ring.map((p) => toLayout(plan, p)) }));
}
